import awardData from "../data/shopStarRankThresholds.json";

const normalizeCategory = (value) =>
  String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const getCategoryValues = (shop) =>
  [
    shop?.rankingBusinessType,
    shop?.businessType,
    shop?.shopCategory?.name,
    shop?.category,
    shop?.subcategory,
    shop?.serviceType,
    shop?.type,
    shop?.marketPlace,
  ].filter(Boolean);

export const getShopStarCategory = (shop) => {
  const values = getCategoryValues(shop).map(normalizeCategory);
  const generalBusiness = awardData.categories.find(
    ({ key }) => key === "generalBusiness",
  );
  const specificMatch = values.reduce((selected, value) => {
    if (selected) return selected;
    return (
      awardData.categories.find(
        ({ aliases, key }) =>
          key !== "generalBusiness" &&
          aliases.some((alias) => value === alias || value.startsWith(alias)),
      ) || null
    );
  }, null);
  const genericMatch = values.some((value) =>
    ["jobscompany", "company", "business", "agency", "trading"].some(
      (alias) => value === alias || value.startsWith(alias),
    ),
  )
    ? generalBusiness
    : null;
  const category =
    specificMatch ||
    genericMatch ||
    awardData.categories.find(({ key }) => key === "retailer");
  return {
    key: category.key,
    label: category.label,
    thresholds: category.milestones,
  };
};

export const getRecommendedReviewCount = (growthNumber) =>
  awardData.recommendedReviewCounts[growthNumber - 1] || null;

export const getShopVerificationStatus = (shop) => {
  if (
    shop?.isVerified === true ||
    shop?.verified === true ||
    shop?.registeredBadge === true
  ) {
    return "Verified";
  }
  const status = String(
    shop?.verificationStatus ||
      shop?.verificationRequest?.status ||
      shop?.shopVerification?.status ||
      "",
  ).toLowerCase();

  if (["approved", "verified", "complete", "completed"].includes(status)) {
    return "Verified";
  }
  if (["pending", "under review", "submitted"].includes(status)) {
    return "Pending";
  }
  if (["rejected", "declined"].includes(status)) return "Rejected";
  return "Unverified";
};

export const getShopStarCount = (shop) => {
  const reportedCount = Number(
    shop?.starCount ?? shop?.totalStars ?? shop?.stars,
  );
  if (Number.isFinite(reportedCount)) {
    return Math.max(0, Math.min(10, Math.floor(reportedCount)));
  }
  return getShopVerificationStatus(shop) === "Verified" ? 2 : 0;
};

export const calculateShopStarRank = ({
  shop,
  verificationStatus,
  itemsCount,
  connections = 0,
  visitors = 0,
  averageRating = 0,
  completedGrowthLevels,
}) => {
  const resolvedStatus = verificationStatus || getShopVerificationStatus(shop);
  const isVerified = resolvedStatus === "Verified";
  const verificationStars = isVerified ? 2 : 0;

  const hasShopDetails = Boolean(
    (shop?.shopname || shop?.companyname || shop?.profileName) &&
    (shop?.description || shop?.desc || shop?.about) &&
    (shop?.shopaddress || shop?.companyaddress || shop?.address)
  );

  const setupEarned =
    hasShopDetails && (typeof itemsCount === "number" ? itemsCount >= 4 : true);
  const setupStars = setupEarned ? 1 : 0;

  let growthStars = 0;
  if (typeof completedGrowthLevels === "number" && completedGrowthLevels >= 0) {
    growthStars = Math.min(7, completedGrowthLevels);
  } else {
    const category = getShopStarCategory(shop);
    growthStars = (category.thresholds || []).filter(
      (m) =>
        connections >= (m.connections || 0) &&
        visitors >= (m.visitors || 0) &&
        (m.rating === 0 || (averageRating >= m.rating && averageRating > 0))
    ).length;
  }

  const totalStars = Math.max(
    0,
    Math.min(10, verificationStars + setupStars + growthStars)
  );
  const starRank = Math.max(1, totalStars);

  return {
    verificationStars,
    setupStars,
    growthStars,
    totalStars,
    starRank,
    isVerified,
    setupEarned,
  };
};

export const readMetric = (shop, keys) => {
  const containers = [
    shop,
    shop?.data,
    shop?.data?.data,
    shop?.stats,
    shop?.metrics,
    shop?.analytics,
    shop?.shopStats,
    shop?.statistics,
    shop?.visitorStats,
    shop?.profileStats,
  ];
  for (const container of containers) {
    for (const key of keys) {
      const rawValue = container?.[key];
      if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
        const value = Number(rawValue);
        if (Number.isFinite(value)) return value;
      }
    }
  }
  return null;
};

const idString = (value) => {
  const id = value && typeof value === "object" ? value._id || value.id : value;
  return id == null ? "" : String(id);
};

export const countShopConnections = (response, shopId, userId) => {
  const payload = [
    response,
    response?.data,
    response?.data?.data,
  ].find(
    (candidate) =>
      Array.isArray(candidate?.connected) ||
      Array.isArray(candidate?.connectedBT),
  ) || response || {};
  const ownIds = new Set([idString(shopId), idString(userId)].filter(Boolean));
  const connected = Array.isArray(payload.connected) ? payload.connected : [];
  const groups = Array.isArray(payload.connectedBT) ? payload.connectedBT : [];
  const counterpartIds = new Set();
  let anonymousCount = 0;
  const addId = (id) => {
    if (id && !ownIds.has(id)) counterpartIds.add(id);
    else if (!id) anonymousCount += 1;
  };

  const addCounterpart = (row) => {
    const senderUser = idString(row?.senderid);
    const receiverUser = idString(row?.recieverid);
    const senderShop = idString(row?.sendershopid);
    const receiverShop = idString(row?.recievershopid);
    const senderIsOwn =
      ownIds.has(senderUser) || ownIds.has(senderShop);
    const receiverIsOwn =
      ownIds.has(receiverUser) || ownIds.has(receiverShop);

    if (senderIsOwn) {
      addId(receiverShop || receiverUser);
      return;
    }
    if (receiverIsOwn) {
      addId(senderShop || senderUser);
      return;
    }

    const candidates = [
      idString(row?.shopid),
      idString(row?.userid),
      idString(row?.shop?._id),
      idString(row?.user?._id),
      idString(row?._id),
      idString(row?.id),
    ];
    const id = candidates.find((candidate) => candidate && !ownIds.has(candidate));
    addId(id);
  };

  connected.forEach(addCounterpart);
  groups.forEach((group) => {
    (Array.isArray(group?.items) ? group.items : [])
      .filter((item) => !item?.isPlaceholder)
      .forEach(addCounterpart);
  });

  const computedCount = counterpartIds.size + anonymousCount;
  if (computedCount > 0) return computedCount;

  // Fallback to explicit totals if individual rows were not populated
  const directTotal = [
    payload?.total,
    payload?.totalItems,
    payload?.totalCount,
    payload?.count,
    response?.total,
    response?.totalItems,
    response?.totalCount,
    response?.count,
  ].find((v) => typeof v === "number" && Number.isFinite(v) && v > 0);

  if (directTotal != null) return directTotal;

  return 0;
};
